'use client';
import { useCallback, useEffect, useState } from 'react';

import useHuddle01 from './useHuddle01';

import type { Room } from '@huddle01/web-core';

import type {
  NewPeerJoined,
  RoomEvents,
  RoomStates,
} from '@huddle01/web-core/types';

export const useRoom = (props?: {
  onJoin?: (data: { room: Room }) => void;
  onWaiting?: (data: RoomEvents['room-waiting'][0]) => void;
  onLeave?: (data: RoomEvents['room-closed'][0]) => void;
  onFailed?: (data: RoomEvents['room-joined-failed'][0]) => void;
  onPeerJoin?: (data: RoomEvents['new-peer-joined'][0]) => void;
  onPeerLeft?: (data: RoomEvents['peer-left'][0]) => void;
}) => {
  const { huddleClient } = useHuddle01();

  const room = huddleClient.room;

  const [state, setState] = useState<RoomStates>(room.state);

  const handleRoomClose = useCallback(
    (data: RoomEvents['room-closed'][0]) => {
      if (props?.onLeave) props.onLeave(data);

      if (data.reason === 'CLOSED') setState('closed');
      else setState('left');
    },
    [props?.onLeave],
  );

  const handleRoomJoin = useCallback(() => {
    if (props?.onJoin) props.onJoin({ room: huddleClient.room });

    setState('connected');
  }, [props?.onJoin]);

  const handleRoomJoinFailed = useCallback(
    (data: RoomEvents['room-joined-failed'][0]) => {
      if (props?.onFailed) props.onFailed(data);

      if (data.status === 'ROOM_ERRORED' || data.status === 'ROOM_NOT_FOUND') {
        setState('failed');
      }
    },
    [props?.onFailed],
  );

  const handlePeerJoin = useCallback(
    ({ peer }: NewPeerJoined) => {
      if (props?.onPeerJoin) {
        props.onPeerJoin({ peer });
      }
    },
    [props?.onPeerJoin],
  );

  const handlePeerLeft = useCallback(
    (data: RoomEvents['peer-left'][0]) => {
      if (props?.onPeerLeft) props.onPeerLeft(data);
    },
    [props?.onPeerLeft],
  );

  const handleRoomConnecting = useCallback(() => {
    setState('connecting');
  }, []);

  useEffect(() => {
    room.on('room-joined', handleRoomJoin);
    room.on('room-closed', handleRoomClose);
    room.on('room-joined-failed', handleRoomJoinFailed);
    room.on('room-connecting', handleRoomConnecting);

    props?.onPeerJoin && room.on('new-peer-joined', handlePeerJoin);
    props?.onPeerLeft && room.on('peer-left', handlePeerLeft);
    props?.onWaiting && room.on('room-waiting', props.onWaiting);
    return () => {
      props?.onPeerJoin && room.off('new-peer-joined', handlePeerJoin);
      props?.onPeerLeft && room.off('peer-left', handlePeerLeft);
      props?.onWaiting && room.off('room-waiting', props.onWaiting);

      room.off('room-joined', handleRoomJoin);

      room.off('room-closed', handleRoomClose);

      room.off('room-joined-failed', handleRoomJoinFailed);

      room.off('room-connecting', handleRoomConnecting);
    };
  }, [
    handleRoomJoin,
    handleRoomClose,
    handlePeerJoin,
    handlePeerLeft,
    handleRoomJoinFailed,
  ]);

  /**
   * Close a particular stream of remote peers
   *  @param data: { label: string; peerIds?: string[] }
   *  @param label: Label of the stream
   *  @param peerIds: PeerIds of the remote peers, if not provided, it will close the stream of all the remote peers
   * @throws { Error } If the Peer Calling this Function is not an admin, then the permissions will not be updated
   */
  const closeStreamOfLabel = useCallback(room.closeStreamOfLabel, []);

  /**
   * Mute everyone in the room. This will close the audio stream of all the remote peers who dont have admin permissions
   *
   * `NOTE: This will target all the audio stream in the room with the label "audio"`
   *
   * @throws { Error } If the Peer Calling this Function is not an admin, then the permissions will not be updated
   */
  const muteEveryone = useCallback(room.muteEveryone, []);

  /**
   * Default method to connect to the Huddle01 Media Room.
   *
   * This method connects to socket, creates a room, and then connects to the room;
   */
  const joinRoom = useCallback(
    (data: {
      roomId: string;
      token: string;
    }) => {
      setState('connecting');

      return huddleClient.joinRoom(data);
    },
    [],
  );

  /**
   * Leave the room and disconnect from the socket
   */
  const leaveRoom = useCallback(huddleClient.leaveRoom, []);

  /**
   * Close the room and disconnect from the socket
   */
  const closeRoom = useCallback(huddleClient.closeRoom, []);

  /**
   * kick peer from room with respective peerId
   * @throws { Error } If the Peer Calling this Function is not an admin, then the permissions will not be updated
   */
  const kickPeer = useCallback(room.kickPeer, []);

  return {
    room,
    state,
    joinRoom,
    leaveRoom,
    closeRoom,
    kickPeer,
    muteEveryone,
    closeStreamOfLabel,
  };
};
